import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  Bolt as BoltIcon,
  Support as SupportIcon,
  Analytics as AnalyticsIcon,
  Api as ApiIcon,
  Engineering as EngineeringIcon,
} from '@mui/icons-material';
import { PricingTier, BillingFrequency } from '../../types/billing';

interface TierSelectionCardProps {
  tier: PricingTier;
  currentTier?: string;
  billingFrequency: BillingFrequency;
  onSelectTier: (tierId: string) => void;
  isLoading?: boolean;
}


const getSupportLevelText = (level: string) => {
  switch (level) {
    case 'community':
      return 'Community Support';
    case 'standard':
      return 'Standard Support (Email)';
    case 'priority':
      return 'Priority Support (24/7)';
    case 'dedicated':
      return 'Dedicated Account Manager';
    default:
      return level;
  }
};

export const TierSelectionCard: React.FC<TierSelectionCardProps> = ({
  tier,
  currentTier,
  billingFrequency,
  onSelectTier,
  isLoading,
}) => {
  const isCurrentTier = currentTier === tier.id;
  const price = tier.price[billingFrequency];
  const monthlyPrice = tier.price.monthly;
  const savings = billingFrequency !== BillingFrequency.MONTHLY
    ? Math.round(((monthlyPrice - price) / monthlyPrice) * 100)
    : 0;

  return (
    <Card
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: (tier.popular || tier.id === 'mep') ? '2px solid' : '1px solid',
        borderColor: tier.id === 'mep' ? '#2e7d32' : (tier.popular ? '#1976d2' : 'rgba(0,0,0,0.12)'),
        boxShadow: (tier.popular || tier.id === 'mep') ? '0 3px 5px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.12)',
      }}
    >
      {(tier.popular || tier.id === 'mep') && (
        <div
          style={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: tier.id === 'mep' ? '#2e7d32' : '#1976d2',
            color: '#fff',
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 4,
            paddingBottom: 4,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {tier.id === 'mep' ? <EngineeringIcon fontSize="small" /> : <StarIcon fontSize="small" />}
          <Typography variant="caption" fontWeight="bold">
            {tier.id === 'mep' ? 'PROFESSIONAL ENGINEERING' : 'MOST POPULAR'}
          </Typography>
        </div>
      )}

      <CardContent style={{ flexGrow: 1, paddingTop: (tier.popular || tier.id === 'mep') ? 32 : 24 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {tier.name}
        </Typography>
        
        <div style={{ marginBottom: 24 }}>
          <Typography variant="h3" component="span" fontWeight="bold">
            ${price}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary">
            /user/{billingFrequency}
          </Typography>
          {savings > 0 && (
            <Chip
              label={`Save ${savings}%`}
              size="small"
              color="success"
              style={{ marginLeft: 8 }}
            />
          )}
        </div>

        <Typography variant="body2" color="text.secondary" paragraph>
          {tier.description}
        </Typography>

        <Divider style={{ marginTop: 16, marginBottom: 16 }} />

        <List dense>
          <ListItem>
            <ListItemIcon>
              <BoltIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={
                tier.features.customAgentLimit === 'unlimited'
                  ? 'Unlimited custom agents'
                  : `Up to ${tier.features.customAgentLimit} custom agents`
              }
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={`${tier.features.baseAgents.length} included ${tier.id === 'mep' ? 'specialized' : 'base'} agents`}
              secondary={tier.id === 'mep' ? 'Includes 5 specialized MEP agents' : tier.features.baseAgents.slice(0, 3).join(', ') + (tier.features.baseAgents.length > 3 ? '...' : '')}
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <SupportIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={getSupportLevelText(tier.features.supportLevel)} />
          </ListItem>

          {tier.features.apiAccess && (
            <ListItem>
              <ListItemIcon>
                <ApiIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Full API access" />
            </ListItem>
          )}

          {tier.features.advancedAnalytics && (
            <ListItem>
              <ListItemIcon>
                <AnalyticsIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Advanced analytics" />
            </ListItem>
          )}

          {tier.id === 'mep' && (
            <ListItem>
              <ListItemIcon>
                <EngineeringIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="MEP Toolkit Included" 
                secondary="CAD processing, HVAC calculations, electrical analysis"
              />
            </ListItem>
          )}

          <ListItem>
            <ListItemIcon>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Token usage billing" 
              secondary={`${tier.features.tokenMarkup * 100}% markup on base cost`}
            />
          </ListItem>
        </List>
      </CardContent>

      <CardActions style={{ padding: 16, paddingTop: 0 }}>
        <Button
          fullWidth
          variant={isCurrentTier ? 'outlined' : 'contained'}
          size="large"
          onClick={() => onSelectTier(tier.id)}
          disabled={isLoading || isCurrentTier}
          style={{ paddingTop: 12, paddingBottom: 12 }}
        >
          {isCurrentTier ? 'Current Plan' : 'Select Plan'}
        </Button>
      </CardActions>
    </Card>
  );
};